import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Dictionary, NumericDictionary, keyBy } from 'lodash'
import classes from './EntityChips.scss'
import { useDialogActions } from 'cityads-v3/hooks/dialog'
import { RootState } from 'cityads-v3/types/reducer'
import { FlattenItem, flatCollection } from 'cityads-v3/utils/arrayUtils_ts'

import Button from '@mui/material/Button'
import Chip, { ChipProps } from '@mui/material/Chip'
import CloseIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';

export type EntityChipsProps = Omit<ChipProps, 'onDelete'> & {
  chipsColors?: NumericDictionary<ChipProps['color']>,
  itemsById: Dictionary<{ name: string }>,
  itemsByIdWithParents?: Dictionary<FlattenItem<FlattenItem<{
    items?: unknown[];
  }>>>,
  groupedLabel?: string,
  value: (number | string)[],
  onMultiDelete?: (id: (number | string)[]) => void,
  onGroupDelete?: () => void,
  withGrouping?: boolean
} & ({
  withGrouping?: true,
  onDelete?: (id?: unknown) => void
} | {
  withGrouping?: false,
  onDelete?: (id?: number | string) => () => void,
})

const MAX_VISIBLE_GROUPED_CHIPS = 2;

export default function EntityChips({
  chipsColors,
  itemsById,
  itemsByIdWithParents,
  groupedLabel,
  onDelete,
  onMultiDelete,
  onGroupDelete,
  value,
  withGrouping = false,
  ...rest
}: EntityChipsProps): JSX.Element {
  const { t } = useTranslation(['main'])
  const isMobile = useSelector((state: RootState) => state.app.isMobile)
  const [dialogOpened, openDialog, closeDialog] = useDialogActions()
  const [multiDelete, setMultiDelete] = useState<(number | string)[]>([]);
  const flattenItems = useMemo(() => keyBy(flatCollection(Object.values(itemsById) as any), 'id'), [itemsById]);

  const handleDelete = useCallback((id: number | string) => {
    if (onMultiDelete) {

      const parents = (itemsByIdWithParents ?? flattenItems)[id]?.parents
      const valueWithParents = parents?.map(item => {
        const res = item as { id: number | string, valueFull: number | string }
        return res.valueFull ?? res.id
      }) ?? [];

      setMultiDelete([
        ...multiDelete,
        ...valueWithParents,
        id
      ])
    } else {
      onDelete(id)
    }
  }, [multiDelete, setMultiDelete, onDelete, onMultiDelete, flattenItems, itemsByIdWithParents])

  const handleApplyDeleted = useCallback(() => {
    if (onMultiDelete) {
      onMultiDelete(multiDelete)
      setMultiDelete([])
    }
    closeDialog()
  }, [multiDelete, onMultiDelete, closeDialog])

  const handleCloseDialog = useCallback(() => {
    closeDialog()
    setMultiDelete([])
  }, [closeDialog])

  const handleRestoreDeleted = useCallback(() => {
    setMultiDelete([])
  }, [setMultiDelete])

  const dialogValues = useMemo(() => value?.filter(id => !multiDelete.includes(id)), [value, multiDelete])

  useEffect(() => {
    if (withGrouping && !value?.length && dialogOpened) {
      closeDialog();
    }
  }, [withGrouping, value, dialogOpened, closeDialog])

  if (!value) { return null; }

  if (withGrouping && value?.length) {
    return (
      <>
        <Chip
          classes={{
            root: classes.chip,
            label: classes.chip__label
          }}
          label={
            `${groupedLabel}: `
            + (value.slice(0, MAX_VISIBLE_GROUPED_CHIPS).map(id => itemsById[id]?.name ?? id).join(', '))
            + ((value.length > MAX_VISIBLE_GROUPED_CHIPS) ? `, +${value.length - MAX_VISIBLE_GROUPED_CHIPS}` : '')
          }
          variant="outlined"
          onClick={onDelete || onMultiDelete ? openDialog : undefined}
          onDelete={onGroupDelete}
          {...rest}
        />
        {onDelete || onMultiDelete ? (
          <Dialog
            classes={{ paper: classes.dialogPaper }}
            fullWidth={isMobile}
            open={dialogOpened}
            onClose={handleCloseDialog}
          >
            <SelectedEntitiesList
              closeDialog={handleCloseDialog}
              groupedLabel={groupedLabel}
              itemsById={itemsById}
              onDelete={handleDelete}
              value={dialogValues}
            />

            {onMultiDelete && multiDelete?.length ? (
              <DialogActions>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleApplyDeleted}
                >
                  {t('buttons.apply')}
                </Button>
                <Button
                  startIcon={<RestoreRoundedIcon />}
                  onClick={handleRestoreDeleted}
                >
                  {t('buttons.cancel')}
                </Button>
              </DialogActions>
            ) : null}
          </Dialog>
        ) : null}
      </>
    )
  }

  return (
    <>
      {value?.map(id => (
        <Chip
          key={id}
          classes={{
            root: classes.chip,
            label: classes.chip__label
          }}
          label={
            itemsById[id]?.name
            ?? id
          }
          color={chipsColors?.[+id]}
          variant="outlined"
          onDelete={onDelete?.(id) as () => void}
          {...rest}
        />
      ))}
    </>
  )
}

type SelectedEntitiesListProps = Pick<EntityChipsProps, 'groupedLabel' | 'itemsById' | 'value'> & {
  onDelete: (value: SelectedEntitiesListProps['value'][0]) => void,
  closeDialog: () => void
}
function SelectedEntitiesList({
  closeDialog,
  groupedLabel,
  itemsById,
  onDelete,
  value,
}: SelectedEntitiesListProps): JSX.Element {
  return (
    <>
      <DialogTitle classes={{ root: classes.dialogTitle }}>
        <div className={classes.dialogHeader}>
          <div>{groupedLabel}</div>

          <IconButton aria-label="close" className={classes.closeButton} onClick={closeDialog} size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <List dense={true}>
          {value.map(value => (
            <ListItem
              key={value}
              secondaryAction={
                onDelete
                  ? (
                    <IconButton edge="end" aria-label="delete" onClick={() => onDelete?.(value)} size="large">
                      <DeleteOutlinedIcon />
                    </IconButton>
                  )
                  : null
              }
            >
              <ListItemButton>
                <ListItemText>{itemsById[value]?.name ?? `ID: ${value}`}</ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </>
  )
}
